import { ComponentClass, SFC } from 'react';
import { COMMON_ROUTE_PATHS, PUBLIC_ROUTE_PATHS, INTERNAL_ROUTE_PATHS, EXTERNAL_ROUTE_PATHS } from '@constants/routes';
import { PermissionCodeConst } from 'skyeye-common-const';
import { PAGE_TITLE } from '@localisation/components';
import {
  Home,
  ForgetPassword,
  RoleList,
  RoleDetail,
  SubscriptionPlanList,
  SubscriptionPlanDetail,
  Subscription,
  UserList,
  UserDetail,
  OrgSelect,
  Account,
  ResetPassword,
  OrganisationList,
  OrganisationDetail,
  CampaignList,
  CampaignDetail,
  CampaignUserList,
  CampaignUserDetail,
  OidcCallback,
  Landing,
  OidcSilent,
  TaasPage,
  AidePage,
  AideScoutManagerPage,
  AideScoutDetail,
  ScoutsPage,
  ErrorPage,
  SeerDemoPage,
  NerdTrainingPage,
  NerdTrainingEntrancePage,
  CompanyDetailPage,
  KeyDevManagerPage, KeyDevClusterManagerPage,
  KeyDevDetail,
  keydevDetailUpdateHistory,
  LogoutRedirect,
  keyDevRequestLogPage,
  KeyDevQualityMonitoringPage,
  LoginPage, 
} from './pages';
import MainSideBar from './components/sidebar/sidebar.component';
import AccountSideBar from './components/sidebar/accountSideBar.component';

interface IRouteProperties {
  path?: string;
  exact?: boolean;
  component: ComponentClass | SFC;
  isPublic?: boolean;
  permissions?: object;
  noAccessRequired?: boolean;
  isCreate?: boolean;
  documentTitle: (match: any) => string;
  withSideBar?: React.ComponentClass | React.SFC;
  showHeader?: boolean;
  authValid?: boolean
}

export const callbackRoute: IRouteProperties = {
  path: COMMON_ROUTE_PATHS.CALLBACK,
  exact: true,
  component: OidcCallback,
  documentTitle: () => PAGE_TITLE.LOGIN,
  authValid: false
};

export const commonRoutes: IRouteProperties[] = [
  {
    path: COMMON_ROUTE_PATHS.ERROR,
    exact: true,
    component: ErrorPage,
    noAccessRequired: true,
    documentTitle: () => PAGE_TITLE.ERROR,
    authValid: false
  },
  {
    path: COMMON_ROUTE_PATHS.ACCOUNT_INFO,
    exact: true,
    component: Account,
    documentTitle: () => PAGE_TITLE.ACC_INFO,
    withSideBar: AccountSideBar,
    authValid: true
  },
  {
    path: COMMON_ROUTE_PATHS.FORGET_PASSWORD,
    exact: true,
    component: ForgetPassword,
    noAccessRequired: true,
    documentTitle: () => PAGE_TITLE.FORGET_PWD,
    authValid: false
  },
  {
    path: COMMON_ROUTE_PATHS.RESET_PASSWORD,
    exact: true,
    component: ResetPassword,
    noAccessRequired: true,
    documentTitle: () => PAGE_TITLE.RESET_PWD,
    authValid: false
  }, {
    path: COMMON_ROUTE_PATHS.LANDING,
    exact: true,
    component: Landing,
    noAccessRequired: true,
    documentTitle: () => PAGE_TITLE.PORTAL,
    authValid: false
  }, {
    path: COMMON_ROUTE_PATHS.SILENT,
    exact: true,
    component: OidcSilent,
    documentTitle: () => PAGE_TITLE.LOGIN,
    authValid: true
  }, {
    path: COMMON_ROUTE_PATHS.LOGIN,
    exact: true,
    component: LoginPage,
    showHeader: false,
    noAccessRequired: true,
    documentTitle: () => PAGE_TITLE.LOGIN,
    authValid: false
  }
];

export const publicRoutes: IRouteProperties[] = [
  {
    path: COMMON_ROUTE_PATHS.MAIN,
    exact: true,
    component: OrgSelect,
    documentTitle: () => PAGE_TITLE.ORG,
    authValid: true
  },
  {
    path: PUBLIC_ROUTE_PATHS.HOME,
    exact: true,
    component: Home,
    permissions: { update: PermissionCodeConst.ORGANISATION_POST_ORGANISATION_UPDATE },
    documentTitle: () => PAGE_TITLE.ORG_INFO,
    withSideBar: MainSideBar,
    authValid: true
  },
  {
    path: PUBLIC_ROUTE_PATHS.ROLES_MANAGEMENT,
    exact: true,
    isPublic: true,
    component: RoleList,
    permissions: {
      list: PermissionCodeConst.RBAC_POST_ROLE_LIST,
      create: PermissionCodeConst.RBAC_POST_ROLE_CREATE,
      update: PermissionCodeConst.RBAC_POST_ROLE_UPDATE,
      delete: PermissionCodeConst.RBAC_POST_ROLE_DELETE,
      read: PermissionCodeConst.RBAC_POST_ROLE_RETRIEVE
    },
    documentTitle: () => PAGE_TITLE.ROLES_SETTINGS,
    withSideBar: MainSideBar,
    authValid: true
  },
  {
    path: PUBLIC_ROUTE_PATHS.ORG_USERS_MANAGEMENT,
    exact: true,
    isPublic: true,
    component: UserList,
    permissions: {
      list: PermissionCodeConst.USER_POST_USER_PUBLIC_LIST,
      create: PermissionCodeConst.USER_POST_USER_PUBLIC_CREATE,
      update: PermissionCodeConst.USER_POST_USER_PUBLIC_UPDATE,
      delete: PermissionCodeConst.USER_POST_USER_PUBLIC_DELETE,
      read: PermissionCodeConst.USER_POST_USER_PUBLIC_RETRIEVE
    },
    documentTitle: () => PAGE_TITLE.USERS_MANAGEMENT,
    withSideBar: MainSideBar,
    authValid: true
  },
  {
    path: PUBLIC_ROUTE_PATHS.ORG_SUB_MANAGEMENT,
    exact: true,
    isPublic: true,
    component: Subscription, // subscription
    permissions: {
      list: PermissionCodeConst.SUBSCRIPTION_POST_SUBSCRIPTION_LIST,
      update: PermissionCodeConst.SUBSCRIPTION_POST_SUBSCRIPTION_UPDATE,
      read: PermissionCodeConst.SUBSCRIPTION_POST_SUBSCRIPTION_RETRIEVE,
      add: PermissionCodeConst.SUBSCRIPTION_POST_SUBSCRIPTION_CREATE
    },
    documentTitle: () => PAGE_TITLE.SUBSCRIPTIONS_MANAGEMENT,
    withSideBar: MainSideBar,
    authValid: true
  },
];

export const internalRoutes: IRouteProperties[] = [
  {
    path: COMMON_ROUTE_PATHS.MAIN,
    exact: true,
    component: Home,
    documentTitle: () => PAGE_TITLE.INTERNAL_HOME,
    withSideBar: MainSideBar,
    authValid: true
  },
  {
    path: INTERNAL_ROUTE_PATHS.ROLES_SETTINGS,
    exact: true,
    component: RoleList,
    isPublic: false,
    permissions: {
      list: PermissionCodeConst.RBAC_POST_ROLE_LIST,
      create: PermissionCodeConst.RBAC_POST_ROLE_CREATE,
      update: PermissionCodeConst.RBAC_POST_ROLE_UPDATE,
      delete: PermissionCodeConst.RBAC_POST_ROLE_DELETE,
      read: PermissionCodeConst.RBAC_POST_ROLE_RETRIEVE
    },
    documentTitle: () => PAGE_TITLE.ROLES_SETTINGS,
    withSideBar: MainSideBar,
    authValid: true
  },
  {
    path: INTERNAL_ROUTE_PATHS.ROLES_SETTINGS_CREATE,
    exact: true,
    component: RoleDetail,
    isPublic: false,
    permissions: {
      list: PermissionCodeConst.RBAC_POST_ROLE_LIST,
      create: PermissionCodeConst.RBAC_POST_ROLE_CREATE,
      update: PermissionCodeConst.RBAC_POST_ROLE_UPDATE,
      delete: PermissionCodeConst.RBAC_POST_ROLE_DELETE,
      read: PermissionCodeConst.RBAC_POST_ROLE_RETRIEVE
    },
    documentTitle: () => PAGE_TITLE.ROLES_SETTINGS,
    isCreate: true,
    withSideBar: MainSideBar,
    authValid: true
  },
  {
    path: INTERNAL_ROUTE_PATHS.ROLES_SETTINGS_INFO,
    exact: true,
    component: RoleDetail,
    isPublic: false,
    permissions: {
      list: PermissionCodeConst.RBAC_POST_ROLE_LIST,
      create: PermissionCodeConst.RBAC_POST_ROLE_CREATE,
      update: PermissionCodeConst.RBAC_POST_ROLE_UPDATE,
      delete: PermissionCodeConst.RBAC_POST_ROLE_DELETE,
      read: PermissionCodeConst.RBAC_POST_ROLE_RETRIEVE
    },
    documentTitle: () => PAGE_TITLE.ROLES_SETTINGS,
    withSideBar: MainSideBar,
    authValid: true
  },
  {
    path: INTERNAL_ROUTE_PATHS.PUBLIC_USERS,
    exact: true,
    isPublic: true,
    component: UserList,
    permissions: {
      list: PermissionCodeConst.USER_POST_USER_PUBLIC_LIST,
      create: PermissionCodeConst.USER_POST_USER_PUBLIC_CREATE,
      update: PermissionCodeConst.USER_POST_USER_PUBLIC_UPDATE,
      delete: PermissionCodeConst.USER_POST_USER_PUBLIC_DELETE,
      read: PermissionCodeConst.USER_POST_USER_PUBLIC_RETRIEVE
    },
    documentTitle: () => PAGE_TITLE.PUBLIC_USERS_MANAGEMENT,
    withSideBar: MainSideBar,
    authValid: true
  },
  {
    path: INTERNAL_ROUTE_PATHS.PUBLIC_USERS_CREATE,
    isCreate: true,
    exact: true,
    isPublic: true,
    component: UserDetail,
    permissions: {
      list: PermissionCodeConst.USER_POST_USER_PUBLIC_LIST,
      create: PermissionCodeConst.USER_POST_USER_PUBLIC_CREATE,
      update: PermissionCodeConst.USER_POST_USER_PUBLIC_UPDATE,
      delete: PermissionCodeConst.USER_POST_USER_PUBLIC_DELETE,
      read: PermissionCodeConst.USER_POST_USER_PUBLIC_RETRIEVE
    },
    documentTitle: () => PAGE_TITLE.PUBLIC_USERS_MANAGEMENT,
    withSideBar: MainSideBar,
    authValid: true
  },
  {
    path: INTERNAL_ROUTE_PATHS.PUBLIC_USERS_INFO,
    exact: true,
    isPublic: true,
    component: UserDetail,
    permissions: {
      list: PermissionCodeConst.USER_POST_USER_PUBLIC_LIST,
      create: PermissionCodeConst.USER_POST_USER_PUBLIC_CREATE,
      update: PermissionCodeConst.USER_POST_USER_PUBLIC_UPDATE,
      delete: PermissionCodeConst.USER_POST_USER_PUBLIC_DELETE,
      read: PermissionCodeConst.USER_POST_USER_PUBLIC_RETRIEVE
    },
    documentTitle: () => PAGE_TITLE.PUBLIC_USERS_MANAGEMENT,
    withSideBar: MainSideBar,
    authValid: true
  },
  {
    path: INTERNAL_ROUTE_PATHS.INTERNAL_USERS,
    exact: true,
    isPublic: false,
    component: UserList,
    permissions: {
      list: PermissionCodeConst.USER_POST_USER_INTERNAL_LIST,
      create: PermissionCodeConst.USER_POST_USER_INTERNAL_CREATE,
      update: PermissionCodeConst.USER_POST_USER_INTERNAL_UPDATE,
      delete: PermissionCodeConst.USER_POST_USER_INTERNAL_DELETE,
      read: PermissionCodeConst.USER_POST_USER_INTERNAL_RETRIEVE
    },
    documentTitle: () => PAGE_TITLE.INTERNAL_USERS_MANAGEMENT,
    withSideBar: MainSideBar,
    authValid: true
  },
  {
    path: INTERNAL_ROUTE_PATHS.INTERNAL_USERS_CREATE,
    isCreate: true,
    exact: true,
    isPublic: false,
    component: UserDetail,
    permissions: {
      list: PermissionCodeConst.USER_POST_USER_INTERNAL_LIST,
      create: PermissionCodeConst.USER_POST_USER_INTERNAL_CREATE,
      update: PermissionCodeConst.USER_POST_USER_INTERNAL_UPDATE,
      delete: PermissionCodeConst.USER_POST_USER_INTERNAL_DELETE,
      read: PermissionCodeConst.USER_POST_USER_INTERNAL_RETRIEVE
    },
    documentTitle: () => PAGE_TITLE.INTERNAL_USERS_MANAGEMENT,
    withSideBar: MainSideBar,
    authValid: true
  },
  {
    path: INTERNAL_ROUTE_PATHS.INTERNAL_USERS_INFO,
    exact: true,
    isPublic: false,
    component: UserDetail,
    permissions: {
      list: PermissionCodeConst.USER_POST_USER_INTERNAL_LIST,
      create: PermissionCodeConst.USER_POST_USER_INTERNAL_CREATE,
      update: PermissionCodeConst.USER_POST_USER_INTERNAL_UPDATE,
      delete: PermissionCodeConst.USER_POST_USER_INTERNAL_DELETE,
      read: PermissionCodeConst.USER_POST_USER_INTERNAL_RETRIEVE
    },
    documentTitle: () => PAGE_TITLE.INTERNAL_USERS_MANAGEMENT,
    withSideBar: MainSideBar,
    authValid: true
  },
  {
    path: INTERNAL_ROUTE_PATHS.CAMPAIGNS,
    exact: true,
    isPublic: false,
    component: CampaignList,
    permissions: {
      list: PermissionCodeConst.CAMPAIGN_EVENT_POST_LIST,
      create: PermissionCodeConst.CAMPAIGN_EVENT_POST_CREATE,
      update: PermissionCodeConst.CAMPAIGN_EVENT_POST_UPDATE,
      delete: PermissionCodeConst.CAMPAIGN_EVENT_POST_DELETE,
      read: PermissionCodeConst.CAMPAIGN_EVENT_POST_RETRIEVE,
      userList: PermissionCodeConst.CAMPAIGN_EVENT_USER_POST_LIST,
      userUpdate: PermissionCodeConst.CAMPAIGN_EVENT_USER_POST_UPDATE
    },
    documentTitle: () => PAGE_TITLE.CAMPAIGNS,
    withSideBar: MainSideBar,
    authValid: true
  },
  {
    path: INTERNAL_ROUTE_PATHS.CAMPAIGNS_CREATE,
    isCreate: true,
    exact: true,
    isPublic: false,
    component: CampaignDetail,
    permissions: {
      list: PermissionCodeConst.CAMPAIGN_EVENT_POST_LIST,
      create: PermissionCodeConst.CAMPAIGN_EVENT_POST_CREATE,
      update: PermissionCodeConst.CAMPAIGN_EVENT_POST_UPDATE,
      delete: PermissionCodeConst.CAMPAIGN_EVENT_POST_DELETE,
      read: PermissionCodeConst.CAMPAIGN_EVENT_POST_RETRIEVE,
      userList: PermissionCodeConst.CAMPAIGN_EVENT_USER_POST_LIST,
      userUpdate: PermissionCodeConst.CAMPAIGN_EVENT_USER_POST_UPDATE
    },
    documentTitle: () => PAGE_TITLE.CAMPAIGNS,
    withSideBar: MainSideBar,
    authValid: true
  },
  {
    path: INTERNAL_ROUTE_PATHS.CAMPAIGNS_INFO,
    exact: true,
    isPublic: false,
    component: CampaignDetail,
    permissions: {
      list: PermissionCodeConst.CAMPAIGN_EVENT_POST_LIST,
      create: PermissionCodeConst.CAMPAIGN_EVENT_POST_CREATE,
      update: PermissionCodeConst.CAMPAIGN_EVENT_POST_UPDATE,
      delete: PermissionCodeConst.CAMPAIGN_EVENT_POST_DELETE,
      read: PermissionCodeConst.CAMPAIGN_EVENT_POST_RETRIEVE,
      userList: PermissionCodeConst.CAMPAIGN_EVENT_USER_POST_LIST,
      userUpdate: PermissionCodeConst.CAMPAIGN_EVENT_USER_POST_UPDATE
    },
    documentTitle: () => PAGE_TITLE.CAMPAIGNS,
    withSideBar: MainSideBar,
    authValid: true
  },
  {
    path: INTERNAL_ROUTE_PATHS.CAMPAIGN_SIGNEES,
    exact: true,
    isPublic: false,
    component: CampaignUserList,
    permissions: {
      list: PermissionCodeConst.CAMPAIGN_USER_POST_LIST,
      create: PermissionCodeConst.CAMPAIGN_USER_POST_CREATE,
      update: PermissionCodeConst.CAMPAIGN_USER_POST_UPDATE,
      delete: PermissionCodeConst.CAMPAIGN_USER_POST_DELETE,
      read: PermissionCodeConst.CAMPAIGN_USER_POST_RETRIEVE
    },
    documentTitle: () => PAGE_TITLE.CAMPAIGN_SIGNEES,
    withSideBar: MainSideBar,
    authValid: true
  },
  {
    path: INTERNAL_ROUTE_PATHS.CAMPAIGN_SIGNEES_CREATE,
    exact: true,
    isCreate: true,
    isPublic: false,
    component: CampaignUserDetail,
    permissions: {
      list: PermissionCodeConst.CAMPAIGN_USER_POST_LIST,
      create: PermissionCodeConst.CAMPAIGN_USER_POST_CREATE,
      update: PermissionCodeConst.CAMPAIGN_USER_POST_UPDATE,
      delete: PermissionCodeConst.CAMPAIGN_USER_POST_DELETE,
      read: PermissionCodeConst.CAMPAIGN_USER_POST_RETRIEVE
    },
    documentTitle: () => PAGE_TITLE.CAMPAIGN_SIGNEES,
    withSideBar: MainSideBar,
    authValid: true
  },
  {
    path: INTERNAL_ROUTE_PATHS.CAMPAIGN_SIGNEES_INFO,
    exact: true,
    isPublic: false,
    component: CampaignUserDetail,
    permissions: {
      list: PermissionCodeConst.CAMPAIGN_USER_POST_LIST,
      create: PermissionCodeConst.CAMPAIGN_USER_POST_CREATE,
      update: PermissionCodeConst.CAMPAIGN_USER_POST_UPDATE,
      delete: PermissionCodeConst.CAMPAIGN_USER_POST_DELETE,
      read: PermissionCodeConst.CAMPAIGN_USER_POST_RETRIEVE
    },
    documentTitle: () => PAGE_TITLE.CAMPAIGN_SIGNEES,
    withSideBar: MainSideBar,
    authValid: true
  },
  {
    path: INTERNAL_ROUTE_PATHS.SUBSCRIPTION_PLANS,
    exact: true,
    component: SubscriptionPlanList,
    permissions: {
      list: PermissionCodeConst.SUBSCRIPTION_PLAN_POST_SUBSCRIPTION_PLAN_LIST,
      create: PermissionCodeConst.SUBSCRIPTION_PLAN_POST_SUBSCRIPTION_PLAN_CREATE,
      update: PermissionCodeConst.SUBSCRIPTION_PLAN_POST_SUBSCRIPTION_PLAN_UPDATE,
      delete: PermissionCodeConst.SUBSCRIPTION_PLAN_POST_SUBSCRIPTION_PLAN_DELETE,
      read: PermissionCodeConst.SUBSCRIPTION_PLAN_POST_SUBSCRIPTION_PLAN_RETRIEVE
    },
    documentTitle: () => PAGE_TITLE.SUBSCRIPTION_PLANS,
    withSideBar: MainSideBar,
    authValid: true
  },
  {
    path: INTERNAL_ROUTE_PATHS.SUBSCRIPTION_PLANS_CREATE,
    isCreate: true,
    exact: true,
    component: SubscriptionPlanDetail,
    permissions: {
      list: PermissionCodeConst.SUBSCRIPTION_PLAN_POST_SUBSCRIPTION_PLAN_LIST,
      create: PermissionCodeConst.SUBSCRIPTION_PLAN_POST_SUBSCRIPTION_PLAN_CREATE,
      update: PermissionCodeConst.SUBSCRIPTION_PLAN_POST_SUBSCRIPTION_PLAN_UPDATE,
      delete: PermissionCodeConst.SUBSCRIPTION_PLAN_POST_SUBSCRIPTION_PLAN_DELETE,
      read: PermissionCodeConst.SUBSCRIPTION_PLAN_POST_SUBSCRIPTION_PLAN_RETRIEVE
    },
    documentTitle: () => PAGE_TITLE.SUBSCRIPTION_PLANS,
    withSideBar: MainSideBar,
    authValid: true
  },
  {
    path: INTERNAL_ROUTE_PATHS.SUBSCRIPTION_PLANS_INFO,
    exact: true,
    component: SubscriptionPlanDetail,
    permissions: {
      list: PermissionCodeConst.SUBSCRIPTION_PLAN_POST_SUBSCRIPTION_PLAN_LIST,
      create: PermissionCodeConst.SUBSCRIPTION_PLAN_POST_SUBSCRIPTION_PLAN_CREATE,
      update: PermissionCodeConst.SUBSCRIPTION_PLAN_POST_SUBSCRIPTION_PLAN_UPDATE,
      delete: PermissionCodeConst.SUBSCRIPTION_PLAN_POST_SUBSCRIPTION_PLAN_DELETE,
      read: PermissionCodeConst.SUBSCRIPTION_PLAN_POST_SUBSCRIPTION_PLAN_RETRIEVE
    },
    documentTitle: () => PAGE_TITLE.SUBSCRIPTION_PLANS,
    withSideBar: MainSideBar,
    authValid: true
  },
  {
    path: INTERNAL_ROUTE_PATHS.ORG,
    exact: true,
    component: OrganisationList,
    permissions: {
      list: PermissionCodeConst.ORGANISATION_POST_ORGANISATION_LIST,
      create: PermissionCodeConst.ORGANISATION_POST_ORGANISATION_CREATE,
      update: PermissionCodeConst.ORGANISATION_POST_ORGANISATION_UPDATE,
      delete: PermissionCodeConst.ORGANISATION_POST_ORGANISATION_DELETE,
      read: PermissionCodeConst.ORGANISATION_POST_ORGANISATION_RETRIEVE
    },
    documentTitle: () => PAGE_TITLE.ORG,
    withSideBar: MainSideBar,
    authValid: true
  },
  {
    path: INTERNAL_ROUTE_PATHS.ORG_CREATE,
    exact: true,
    component: OrganisationDetail,
    permissions: {
      list: PermissionCodeConst.ORGANISATION_POST_ORGANISATION_LIST,
      create: PermissionCodeConst.ORGANISATION_POST_ORGANISATION_CREATE,
      update: PermissionCodeConst.ORGANISATION_POST_ORGANISATION_UPDATE,
      delete: PermissionCodeConst.ORGANISATION_POST_ORGANISATION_DELETE,
      read: PermissionCodeConst.ORGANISATION_POST_ORGANISATION_RETRIEVE
    },
    documentTitle: () => PAGE_TITLE.ORG_MANAGEMENT,
    isCreate: true,
    withSideBar: MainSideBar,
    authValid: true
  },
  {
    path: INTERNAL_ROUTE_PATHS.ORG_INFO,
    exact: true,
    component: OrganisationDetail,
    permissions: {
      list: PermissionCodeConst.ORGANISATION_POST_ORGANISATION_LIST,
      update: PermissionCodeConst.ORGANISATION_POST_ORGANISATION_UPDATE
    },
    documentTitle: () => PAGE_TITLE.ORG_INFO,
    withSideBar: MainSideBar,
    authValid: true
  },

  // {
  //   path: INTERNAL_ROUTE_PATHS.ORG_MANAGEMENT_ROLES,
  //   exact: true,
  //   isPublic: true,
  //   component: RolesManagement,
  //   permissions: {
  //     list: PermissionCodeConst.RBAC_POST_ROLE_LIST,
  //     create: PermissionCodeConst.RBAC_POST_ROLE_CREATE,
  //     update: PermissionCodeConst.RBAC_POST_ROLE_UPDATE,
  //     delete: PermissionCodeConst.RBAC_POST_ROLE_DELETE,
  //     read: PermissionCodeConst.RBAC_POST_ROLE_RETRIEVE
  //   },
  //   documentTitle: () => PAGE_TITLE.ROLES_SETTINGS
  // },
  // {
  //   path: INTERNAL_ROUTE_PATHS.ORG_MANAGEMENT_USERS,
  //   exact: true,
  //   isPublic: true,
  //   component: UsersManagement,
  //   permissions: {
  //     list: PermissionCodeConst.USER_POST_USER_PUBLIC_LIST,
  //     create: PermissionCodeConst.USER_POST_USER_PUBLIC_CREATE,
  //     update: PermissionCodeConst.USER_POST_USER_PUBLIC_UPDATE,
  //     delete: PermissionCodeConst.USER_POST_USER_PUBLIC_DELETE,
  //     read: PermissionCodeConst.USER_POST_USER_PUBLIC_RETRIEVE
  //   },
  //   documentTitle: () => PAGE_TITLE.USERS_MANAGEMENT
  // },
  // {
  //   path: INTERNAL_ROUTE_PATHS.ORG_MANAGEMENT_SUB,
  //   exact: true,
  //   component: Subscription, // subscription
  //   permissions: {
  //     list: PermissionCodeConst.SUBSCRIPTION_POST_SUBSCRIPTION_LIST,
  //     update: PermissionCodeConst.SUBSCRIPTION_POST_SUBSCRIPTION_UPDATE,
  //     read: PermissionCodeConst.SUBSCRIPTION_POST_SUBSCRIPTION_RETRIEVE,
  //     add: PermissionCodeConst.SUBSCRIPTION_POST_SUBSCRIPTION_CREATE
  //   },
  //   documentTitle: () => PAGE_TITLE.SUBSCRIPTIONS_MANAGEMENT
  // },
  {
    path: INTERNAL_ROUTE_PATHS.TAAS,
    exact: true,
    component: TaasPage,
    documentTitle: () => PAGE_TITLE.SCOUT_TRAINING,
    authValid: true
  },
  {
    path: INTERNAL_ROUTE_PATHS.AIDE,
    exact: true,
    component: AidePage,
    permissions: {
      list: PermissionCodeConst.SCOUTAI_SCOUTMASTER_POST_SCOUTAI_LIST,
    },
    documentTitle: () => PAGE_TITLE.AIDE,
    authValid: true
  },
  {
    path: INTERNAL_ROUTE_PATHS.AIDE_SCOUT_MANAGER,
    exact: true,
    component: AideScoutManagerPage,
    permissions: {
      list: PermissionCodeConst.SCOUTAI_SCOUTMASTER_POST_SCOUTAI_LIST,
      create: PermissionCodeConst.SCOUTAI_SCOUTMASTER_POST_SCOUTAI_CREATE,
      update: PermissionCodeConst.SCOUTAI_SCOUTMASTER_POST_SCOUTAI_UPDATE,
      read: PermissionCodeConst.SCOUTAI_SCOUTMASTER_POST_SCOUTAI_RETRIEVE
    },
    documentTitle: () => PAGE_TITLE.AIDE_SCOUT_MANAGER,
    authValid: true
  },
  {
    path: INTERNAL_ROUTE_PATHS.AIDE_SCOUT_MANAGER_CREATE,
    exact: true,
    component: AideScoutDetail,
    permissions: {
      list: PermissionCodeConst.SCOUTAI_SCOUTMASTER_POST_SCOUTAI_LIST,
      create: PermissionCodeConst.SCOUTAI_SCOUTMASTER_POST_SCOUTAI_CREATE,
      update: PermissionCodeConst.SCOUTAI_SCOUTMASTER_POST_SCOUTAI_UPDATE,
      read: PermissionCodeConst.SCOUTAI_SCOUTMASTER_POST_SCOUTAI_RETRIEVE
    },
    documentTitle: () => PAGE_TITLE.AIDE_SCOUT_MANAGER,
    authValid: true
  },
  {
    path: INTERNAL_ROUTE_PATHS.AIDE_SCOUT_MANAGER_INFO,
    exact: true,
    component: AideScoutDetail,
    permissions: {
      list: PermissionCodeConst.SCOUTAI_SCOUTMASTER_POST_SCOUTAI_LIST,
      create: PermissionCodeConst.SCOUTAI_SCOUTMASTER_POST_SCOUTAI_CREATE,
      update: PermissionCodeConst.SCOUTAI_SCOUTMASTER_POST_SCOUTAI_UPDATE,
      read: PermissionCodeConst.SCOUTAI_SCOUTMASTER_POST_SCOUTAI_RETRIEVE,
      share: PermissionCodeConst.SHARE_SCOUTAI_POST_SHARE_SCOUTAI_TO_USER
    },
    documentTitle: () => PAGE_TITLE.AIDE_SCOUT_MANAGER,
    authValid: true,
  },
  {
    path: INTERNAL_ROUTE_PATHS.AIDE_NERD_TRAINING_ENTRANCE,
    exact: true,
    component: NerdTrainingEntrancePage,
    documentTitle: () => PAGE_TITLE.AIDE_NERD_TRAINING,
    authValid: true,
  },
  {
    path: INTERNAL_ROUTE_PATHS.AIDE_NERD_TRAINING,
    exact: true,
    component: NerdTrainingPage,
    documentTitle: () => PAGE_TITLE.AIDE_NERD_TRAINING,
    authValid: true,
  },
  {
    path: INTERNAL_ROUTE_PATHS.ALL_SCOUTS,
    exact: true,
    component: ScoutsPage,
    documentTitle: () => PAGE_TITLE.SCOUT_AIS,
    authValid: true
  },
  {
    path: INTERNAL_ROUTE_PATHS.SEER_DEMO,
    exact: true,
    component: SeerDemoPage,
    permissions: {
      add: PermissionCodeConst.SCOUTAI_SEER_PREDICTION_ADD,
      read: PermissionCodeConst.SCOUTAI_SEER_PREDICTION_PEEK_STATUS,
      list: PermissionCodeConst.SCOUTAI_SEER_PREDICTION_RETRIEVE,
    },
    documentTitle: () => PAGE_TITLE.SEER_DEMO,
    authValid: true
  },
  {
    path: EXTERNAL_ROUTE_PATHS.COMPANY_DETAIL,
    exact: true,
    component: CompanyDetailPage,
    documentTitle: () => PAGE_TITLE.COMPANY_DETAIL,
    authValid: true,
  },
  // {
  //   path: INTERNAL_ROUTE_PATHS.KEY_DEV_MANAGER,
  //   exact: true,
  //   component: KeyDevManagerPage,
  //   documentTitle: () => PAGE_TITLE.KEY_DEV_MANAGER,
  //   authValid: true
  // },
  // {
  //   path: INTERNAL_ROUTE_PATHS.KEY_DEV_MANAGER_DETAIL,
  //   exact: true,
  //   component: KeyDevDetail,
  //   documentTitle: () => PAGE_TITLE.KEY_DEV_DETAIL,
  //   authValid: true
  // },
  // {
  //   path: INTERNAL_ROUTE_PATHS.KEY_DEV_MANAGER_DETAIL_UPDATE_HISTORY,
  //   exact: true,
  //   component: keydevDetailUpdateHistory,
  //   documentTitle: () => PAGE_TITLE.KEY_DEV_DETAIL_UPDATE_HISTORY,
  //   authValid: true
  // },
  // {
  //   path: INTERNAL_ROUTE_PATHS.KEY_DEV_CLUSTER_MANAGEMENT,
  //   exact: true,
  //   component: KeyDevClusterManagerPage,
  //   documentTitle: () => PAGE_TITLE.KEY_DEV_CLUSTER_MANAGEMENT,
  //   authValid: true
  // },
  // {
  //   path: INTERNAL_ROUTE_PATHS.KEY_DEV_REQUEST_LOG,
  //   exact: true,
  //   component: keyDevRequestLogPage,
  //   documentTitle: () => PAGE_TITLE.KEY_DEV_REQUEST_LOG,
  //   authValid: true
  // },
  // {
  //   path: INTERNAL_ROUTE_PATHS.KEY_DEV_QUALITY_MONITORING,
  //   exact: true,
  //   component: KeyDevQualityMonitoringPage,
  //   documentTitle: () => PAGE_TITLE.KEY_DEV_REQUEST_LOG,
  //   authValid: true
  // }
];
